/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
'use client';

import { useEffect } from 'react';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import type { AppConfig } from '@/types/config';
import { useTrackingConsent } from '@/hooks/use-tracking-consent';
import { TrackingConsent } from '@/types/tracking-consent';

const SCRIPT_ID = 'quantum-metrics-sdk';

/**
 * Loads the Quantum Metrics script once per page session, consent-gated.
 *
 * The script is injected asynchronously (non-blocking) into <head> after
 * tracking consent is accepted. Subsequent renders are no-ops — the DOM
 * guard (`getElementById`) prevents double-injection.
 *
 * Script URL: https://cdn.quantummetric.com/qscripts/quantum-ashleyfurniture.js
 * Configure via PUBLIC__app__engagement__quantumMetrics__scriptUrl to override
 * per environment (e.g. a QM staging/sandbox URL for non-prod targets).
 */
export function QuantumMetrics() {
    const config = useConfig<AppConfig>();
    const { trackingConsent } = useTrackingConsent();

    useEffect(() => {
        if (typeof window === 'undefined') return;
        if (trackingConsent !== TrackingConsent.Accepted) return;

        const qmConfig = config.engagement.quantumMetrics;
        if (!qmConfig?.enabled || !qmConfig.scriptUrl) return;

        // Already injected — nothing to do
        if (document.getElementById(SCRIPT_ID)) return;

        // QM internally reads window.dataLayer for its tag triggers — must exist before the script runs
        window.dataLayer = window.dataLayer || [];

        const script = document.createElement('script');
        script.id = SCRIPT_ID;
        script.src = qmConfig.scriptUrl;
        script.async = true;
        script.defer = true;
        document.head.appendChild(script);
    }, [trackingConsent, config.engagement.quantumMetrics]);

    return null;
}
